// A charge (qa-runs#366; docs/MRCS/feats.md §10b): a kind of move (`distance.movement.kinds`) that carries `charge`, the
// system's numbers. A system with no such kind has no charge and the table shows nothing. Pure: the mover's screen and
// the server each work the same answer out, the server from what THAT person was sent, so a refusal never tells of a
// creature or a zone they cannot see.
//
// Where a charge may end: for each creature not on the mover's side, the nearest squares from which it can be attacked
// (next to it, or as far off as the token's own melee weapon reaches), when those are far enough, within the move,
// free, and joined to the start by a clear straight line: no wall, no difficult terrain, no creature in any square a line
// between the two passes through, unless it is helpless or dead.
import { axisPositions } from '../grid/cells.js';
import { isGridless } from '../grid/grid.js';
import { squaresFor } from '../grid/measure.js';
import { cellKey } from '../grid/reach.js';
import { inBand } from './weaponReach.js';
import { routeBlocked } from '../grid/walls.js';
import { footprintSquares } from '../state/fog.js';
import { dispositionOf } from '../state/objects.js';
import { spanTest } from '../state/zones.js';
import { conditionKey } from './chainTable.js';
import { HELPLESS_CONDITIONS } from './triggers.js';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isText = (v, most = 60) => typeof v === 'string' && v.trim().length > 0 && v.length <= most;
const only = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
const isTimes = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 2 && v <= 10;
const isMod = (v) => typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= 50;
function parseFeat(v) {
    if (!isObj(v) || !only(v, ['feat', 'mounted', 'after', 'spares', 'times', 'weapons']) || !isText(v.feat))
        return null;
    for (const k of ['mounted', 'after', 'spares'])
        if (v[k] !== undefined && v[k] !== true)
            return null;
    if (v.times !== undefined && !isTimes(v.times))
        return null;
    const weapons = [];
    if (v.weapons !== undefined) {
        if (!Array.isArray(v.weapons) || v.weapons.length > 10)
            return null;
        for (const w of v.weapons) {
            if (!isObj(w) || !only(w, ['name', 'times']) || !isText(w.name) || !isTimes(w.times))
                return null;
            weapons.push({ name: w.name.trim().toLowerCase(), times: w.times });
        }
    }
    return { feat: v.feat.trim(), ...(v.mounted ? { mounted: true } : {}), ...(v.after ? { after: true } : {}), ...(v.spares ? { spares: true } : {}), ...(isTimes(v.times) ? { times: v.times } : {}), ...(weapons.length ? { weapons } : {}) };
}
/** A kind of move's `charge`, strictly: null when any part is malformed, so the system's movement is refused whole. The data's keys and the view's are the same. */
export function parseCharge(v) {
    if (!isObj(v) || !only(v, ['least', 'attack', 'ac', 'mounted', 'feats']))
        return null;
    if (typeof v.least !== 'number' || !(v.least >= 0) || v.least > 1000 || !isMod(v.attack) || !isMod(v.ac))
        return null;
    if (v.mounted !== undefined && !isText(v.mounted))
        return null;
    const list = v.feats === undefined ? [] : v.feats;
    if (!Array.isArray(list) || list.length > 20)
        return null;
    const feats = list.map(parseFeat);
    if (!feats.every((f) => f !== null))
        return null;
    return { least: v.least, attack: v.attack, ac: v.ac, ...(typeof v.mounted === 'string' ? { mounted: v.mounted.trim() } : {}), feats };
}
/** Whether a token is mounted: it carries the system's condition for it, any case. */
export const isMounted = (values, rule) => {
    const c = rule.mounted?.toLowerCase();
    return !!c && (values?.conditions ?? []).some((x) => x.trim().toLowerCase() === c);
};
/** The feats that change this token's charge: those it carries, a mounted one only while mounted. */
export const chargeFeatsOf = (rule, values, has) => rule.feats.filter((f) => has(f.feat) && (!f.mounted || isMounted(values, rule)));
/** How many times over a charge's attack deals its damage with this weapon; 1, as it stands. */
export function chargeTimes(feats, weapon) {
    const name = weapon.toLowerCase();
    return feats.reduce((most, f) => Math.max(most, f.weapons?.find((w) => name.includes(w.name))?.times ?? f.times ?? 1), 1);
}
/** The squares of a footprint's top-left a line between two passes through: both of them where it runs along a corner or an edge. */
export function lineSquares(from, to) {
    const di = to.i - from.i;
    const dj = to.j - from.j;
    const steps = 2 * Math.max(Math.abs(di), Math.abs(dj));
    const out = new Map();
    const add = (i, j) => { const c = { i, j }; out.set(cellKey(c), c); };
    for (let n = 0; n <= steps; n += 1) {
        const x = from.i + (di * n) / (steps || 1);
        const y = from.j + (dj * n) / (steps || 1);
        for (const i of new Set([Math.floor(x + 0.5 - 1e-9), Math.ceil(x - 0.5 + 1e-9)]))
            for (const j of new Set([Math.floor(y + 0.5 - 1e-9), Math.ceil(y - 0.5 + 1e-9)]))
                add(i, j);
    }
    return [...out.values()];
}
/** Where a charge may end, as the board draws a reach; `targets`, by end square, the creatures it reaches from there. Null on a gridless map. */
export function chargeEnds(c) {
    if (isGridless(c.grid))
        return null;
    const xs = axisPositions(c.grid, c.extent, 'x');
    const ys = axisPositions(c.grid, c.extent, 'y');
    const fp = footprintSquares(c.grid, c.extent, c.token, c.token.size);
    const w = fp.i1 - fp.i0 + 1;
    const h = fp.j1 - fp.j0 + 1;
    const start = { i: fp.i0, j: fp.j0 };
    const columns = xs.length - 1;
    const rows = ys.length - 1;
    const side = dispositionOf(c.token);
    const here = c.things.filter((o) => o.id !== c.token.id && (o.kind === undefined || o.kind === 'token') && (o.floor ?? null) === (c.token.floor ?? null));
    const held = new Set();
    const blocking = new Set();
    const placed = here.map((o) => ({ o, f: footprintSquares(c.grid, c.extent, o, o.size) }));
    for (const { o, f } of placed) {
        const stops = !o.dead && !(o.values?.conditions ?? []).some((x) => HELPLESS_CONDITIONS.includes(conditionKey(x)));
        for (let j = f.j0; j <= f.j1; j += 1)
            for (let i = f.i0; i <= f.i1; i += 1) {
                held.add(`${i},${j}`);
                if (stops)
                    blocking.add(`${i},${j}`);
            }
    }
    const slow = spanTest(c.difficult);
    const centre = (x) => ({ x: (xs[x.i] + xs[Math.min(columns, x.i + w)]) / 2, y: (ys[x.j] + ys[Math.min(rows, x.j + h)]) / 2 });
    const inFoot = (x, test) => {
        for (let j = x.j; j < x.j + h; j += 1)
            for (let i = x.i; i < x.i + w; i += 1)
                if (test({ i, j }))
                    return true;
        return false;
    };
    const clear = (end) => {
        if (routeBlocked(centre(start), [centre(end)], c.walls))
            return false;
        if (inFoot(end, (s) => held.has(cellKey(s))))
            return false;
        return lineSquares(start, end).every((x) => !inFoot(x, (s) => {
            const mine = s.i >= fp.i0 && s.i <= fp.i1 && s.j >= fp.j0 && s.j <= fp.j1;
            return !mine && (blocking.has(cellKey(s)) || (c.distance.difficult !== null && slow(s)));
        }));
    };
    const least = c.rule.least / c.distance.perSquare;
    const bands = (c.bands ?? []).filter((b) => b.far >= c.distance.perSquare);
    const out = bands.length ? Math.ceil(Math.max(...bands.map((b) => b.far)) / c.distance.perSquare) : 1;
    const cells = new Map();
    const targets = new Map();
    const cost = (i, j) => squaresFor(i - start.i, j - start.j, c.distance.diagonal);
    for (const { o, f } of placed) {
        if (o.dead || dispositionOf(o) === side)
            continue;
        const near = [];
        for (let j = f.j0 - h + 1 - out; j <= f.j1 + out; j += 1) {
            for (let i = f.i0 - w + 1 - out; i <= f.i1 + out; i += 1) {
                const gx = Math.max(0, f.i0 - (i + w - 1), i - f.i1);
                const gy = Math.max(0, f.j0 - (j + h - 1), j - f.j1);
                if (!gx && !gy)
                    continue;
                if (bands.length ? bands.some((b) => inBand(b, squaresFor(gx, gy, c.distance.diagonal) * c.distance.perSquare)) : Math.max(gx, gy) === 1)
                    near.push({ i, j });
            }
        }
        // The nearest of them all, on the map or taken: when the nearest is blocked there is no charge at this creature.
        const on = near.filter((x) => x.i >= 0 && x.j >= 0 && x.i + w <= columns && x.j + h <= rows);
        if (!on.length)
            continue;
        const best = Math.min(...on.map((x) => cost(x.i, x.j)));
        if (best < least - 1e-9 || best > c.budget + 1e-9)
            continue;
        for (const x of on) {
            if (cost(x.i, x.j) > best + 1e-9 || !clear(x))
                continue;
            const k = cellKey(x);
            cells.set(k, best);
            targets.set(k, [...(targets.get(k) ?? []), o.id]);
        }
    }
    return { reach: { cells, w, h, xs, ys }, targets };
}
export const CHARGE_NOT_NOW = 'A charge takes the whole turn: only as the first thing the token does on its own turn in a fight.';
export const CHARGE_NOT_HERE = 'No charge ends there: the nearest square to a foe, at least the shortest charge away, along a clear straight line.';
export const CHARGE_LINE = 'A charge goes in a straight line.';
export const CHARGE_ATTACK_FIRST = 'A charge moves on only after its attack.';
export const CHARGE_ENDED = 'A charge ends at its attack.';
/** A charge's end from its route: one point, a square `chargeEnds` holds. The creatures it reaches, or why not. */
export function chargeTargets(c, route) {
    if (route.length !== 1)
        return CHARGE_LINE;
    const ends = chargeEnds(c);
    // A route's point is the token's centre; the reach keys its footprint's top-left square.
    const fp = footprintSquares(c.grid, c.extent, route[0], c.token.size);
    return ends?.targets.get(cellKey({ i: fp.i0, j: fp.j0 })) ?? CHARGE_NOT_HERE;
}
/** Ride-By Attack's way on, after the attack: from where the charge ended, on along its line, half a square's grace. Only the move's end counts: like the charge, it is the straight line there, however the hand went. */
export function chargeRestRefusal(open, end, perSide) {
    if (!open.after)
        return CHARGE_ENDED;
    if (!open.attacked)
        return CHARGE_ATTACK_FIRST;
    if (!end)
        return CHARGE_LINE;
    const dx = open.to.x - open.from.x;
    const dy = open.to.y - open.from.y;
    const ex = end.x - open.to.x;
    const ey = end.y - open.to.y;
    const len = Math.hypot(dx, dy) || 1;
    const off = Math.abs(dx * ey - dy * ex) / len;
    return dx * ex + dy * ey > 0 && off <= perSide / 2 + 1e-9 ? null : CHARGE_LINE;
}
//# sourceMappingURL=charge.js.map